import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Box,
  Modal,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import axiosInstance from "../../../API/axiosInstance";
import * as XLSX from "xlsx";

const FleetDailyReport = () => {
  const [speedSelection, setSpeedSelection] = useState<string>("");
  const [vehicleStatus, setVehicleStatus] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [rcSuccess, setRcSuccess] = useState(null);
  const [open, setOpen] = useState(false);
  const [insuranceSuccess, setInsuranceSuccess] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSuccessSnackbar, setIsSuccessSnackbar] = useState(false);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    imei: "",
    bleMac: "",
    msisdn: "",
    iotType: "",
    vehicleNo: "",
    vehicleSpeedType: "",
    vehicleAgeType: "",
  });
  const handleDownloadTemplate = () => {
    const worksheetData = [
      ["imei", "bleMac", "msisdn", "iotType", "vehicleNo", "speedSelection", "vehicleStatus"],
      ["", "", "", "", "", "", ""],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    XLSX.writeFile(workbook, "Create_Ev_Template.xlsx");
  };

  const handleFileChange = (event: any) => {
    setSelectedFile(event.target.files[0]);
  };
  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setSnackbarMessage("Please select a file before submitting.");
      setShowErrorSnackbar(true);
      return;
    }

    const formData = new FormData();
    formData.append("fileItem", selectedFile);

    try {
      const response = await axiosInstance.post(
        "/users/fleet-ease/bulk-upload/vehicle",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.status === 200 && response?.data?.code === 2000) {
        const downloadURL = response?.data?.data?.data?.downloadURL;
        
        setBulkModalOpen(false);
        setSnackbarMessage("File uploaded successfully.");
        setIsSuccessSnackbar(true);
        setShowErrorSnackbar(true);

        if (downloadURL) {
          window.open(downloadURL, "_blank");
        }

        setTimeout(() => {
          navigate("/evdetails/ev-details-list", { state: { tabIndex: 2 } });
        }, 3000); // Delay of 3 seconds
      } else {
        setSnackbarMessage(response.data?.message || "Error uploading file.");
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      setSnackbarMessage("Failed to upload file. Please try again.");
      setShowErrorSnackbar(true);
    }
  };
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };
  const RequiredLabel = ({ label }: { label: string }) => (
    <span>
      {label}
      <span style={{ color: "red" }}> *</span>
    </span>
  );
  const handleFileUpload = async (event: any, fileType: any) => {
    const file = event.target.files[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append("file", file);

    try {
      const response = await axiosInstance.post(
        "/file/upload-image",
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response?.data?.data?.fileId) {
        if (fileType === "rcDocId") {
          setRcSuccess(response.data.message);
        } else if (fileType === "insuranceDocId") {
          setInsuranceSuccess(response.data.message);
        }
        setFormData((prevData) => ({
          ...prevData,
          [fileType]: response.data.data.fileId,
        }));
      }
    } catch (error) {
      console.error("File upload failed:", error);
      setSnackbarMessage("File upload failed. Please try again.");
      setShowErrorSnackbar(true);
    }
  };
  const handleBackToDashboard = () => {
    navigate("/evdetails/ev-details-list"); // Navigate back to Dashboard with EV Details tab active
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      vehicleSpeedType: speedSelection ? speedSelection.toUpperCase() : "",
      vehicleAgeType: vehicleStatus ? vehicleStatus.toUpperCase() : "",
    }));
  }, [speedSelection, vehicleStatus]);
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post(
        "/users/fleet-ease/api/create-vehicle",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.status === 200) {
        setOpen(true);
        setTimeout(() => {
          setOpen(false);
          navigate("/evdetails/ev-details-list", { state: { tabIndex: 2 } });
        }, 5000);
      } else {
        setSnackbarMessage(response.data?.message || "Error creating user");
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error("API call error:", error);
      if (axios.isAxiosError(error) && error.response) {
        setSnackbarMessage(
          error.response.data?.message || "Failed to update. Please try again."
        );
      } else {
        setSnackbarMessage("Network error or server unreachable.");
      }
      setShowErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex bg-white min-h-80 box-shadow p-3 rounded-xl mx-16 mt-10">
        <div className="w-full">
          <Grid
            container
            spacing={5}
            className="w-full flex justify-between items-center"
          >
            <Grid item md={3}>
              <div className="flex ml-4">
                <h2 className="text-center ml-2 mx-1 mt-2 font-semibold text-2xl text-black pb-2">
                  <IconButton onClick={handleBackToDashboard}>
                    <ArrowBackIcon />
                  </IconButton>
                  New EV Detail
                </h2>
              </div>
            </Grid>
            <Grid item md={9} className="flex justify-end align-center">
              <Button
                onClick={() => setBulkModalOpen(true)}
                style={{
                  backgroundColor: "#00cd5b",
                  color: "#fff",
                  borderRadius: "40px",
                  marginRight: "10px",
                }}
              >
                <UploadIcon /> Bulk Upload
              </Button>
              <Button
                onClick={handleDownloadTemplate}
                type="submit"
                style={{
                  backgroundColor: "#00cd5b",
                  padding: "5px 10px",
                  color: "#fff",
                  borderRadius: "40px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                <DownloadIcon /> Download Template
              </Button>
            </Grid>
          </Grid>
          <hr className="my-4" />
          <form onSubmit={handleSubmit} className="p-8 w-full">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={<RequiredLabel label="IMEI" />}
                  name="imei"
                  value={formData.imei}
                  onChange={handleChange}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={<RequiredLabel label="Mac Address" />}
                  name="bleMac"
                  value={formData.bleMac}
                  onChange={handleChange}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={<RequiredLabel label="Msisdn" />}
                  name="msisdn"
                  value={formData.msisdn}
                  onChange={handleChange}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={<RequiredLabel label="Iot Type" />}
                  name="iotType"
                  value={formData.iotType}
                  onChange={handleChange}
                  variant="standard"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className="pt-10">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label={<RequiredLabel label="Vehicle No" />}
                  name="vehicleNo"
                  value={formData.vehicleNo}
                  onChange={handleChange}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  label={<RequiredLabel label="Speed Selection" />}
                  name="speedSelection"
                  value={speedSelection}
                  onChange={(e) => setSpeedSelection(e.target.value)}
                  variant="standard"
                  size="small"
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "8px 0",
                    },
                  }}
                >
                  <option value=""></option>
                  <option value="High">High Speed</option>
                  <option value="Low">Low Speed</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  label={<RequiredLabel label="Vehicle Status" />}
                  name="vehicleStatus"
                  value={vehicleStatus}
                  onChange={(e) => setVehicleStatus(e.target.value)}
                  variant="standard"
                  size="small"
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "8px 0",
                    },
                  }}
                >
                  <option value=""></option>
                  <option value="New">New</option>
                  <option value="Old">Old</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Upload RC"
                  InputLabelProps={{ shrink: true }}
                  type="file"
                  onChange={(e) => handleFileUpload(e, "rcDocId")}
                  sx={{
                    border: "none",
                    borderBottom: "1px solid",
                    borderRadius: 0,
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-input": { padding: "12.5px 9px" },
                  }}
                />


                {rcSuccess && <p>{rcSuccess}</p>}
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Upload Insurance"
                  InputLabelProps={{ shrink: true }}
                  type="file"
                  onChange={(e) => handleFileUpload(e, "insuranceDocId")}
                  className="mt-16"
                  sx={{
                    border: "none",
                    borderBottom: "1px solid",
                    borderRadius: 0,
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-input": { padding: "12.5px 9px" },
                  }}
                />

                {insuranceSuccess && <p>{insuranceSuccess}</p>}
              </Grid>
            </Grid>
            <Grid container spacing={2} className="pt-10 flex justify-end">
              <Grid item xs={2}>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.imei.trim() ||
                    !formData.bleMac.trim() ||
                    !formData.msisdn.trim() ||
                    !formData.iotType.trim() ||
                    !formData.vehicleNo.trim() ||
                    !formData.vehicleSpeedType ||
                    !formData.vehicleAgeType
                  }
                  style={{
                    backgroundColor:
                      isSubmitting ||
                      !formData.imei.trim() ||
                      !formData.bleMac.trim() ||
                      !formData.msisdn.trim() ||
                      !formData.iotType.trim() ||
                      !formData.vehicleNo.trim() ||
                      !formData.vehicleSpeedType ||
                      !formData.vehicleAgeType
                        ? "#c4c4c4"
                        : "#000",
                    padding: "5px 18px",
                    color: "#fff",
                    borderRadius: "40px",
                    width: "100%",
                    textTransform: "none",
                    fontSize: "16px",
                    cursor:
                      isSubmitting ||
                      !formData.imei.trim() ||
                      !formData.bleMac.trim() ||
                      !formData.msisdn.trim() ||
                      !formData.iotType.trim() ||
                      !formData.vehicleNo.trim() ||
                      !formData.vehicleSpeedType ||
                      !formData.vehicleAgeType
                        ? "not-allowed"
                        : "pointer",
                    transition: "background-color 0.3s ease-in-out",
                  }}
                >
                  {isSubmitting ? "Creating..." : "Create Vehicle"}
                </Button>
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ textAlign: "center", mt: 1, }}
                >
                  *fields are mandatory
                </Typography>
              </Grid>
            </Grid>
          </form>
        </div>
        <Modal open={bulkModalOpen} onClose={() => setBulkModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              p: 4,
              borderRadius: 2,
              boxShadow: 24,
            }}
          >
            <IconButton
              sx={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setBulkModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6">Bulk Upload</Typography>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ marginTop: 16 }}
            />
            {/* {selectedFile && <Typography variant="body2" sx={{ mt: 1 }}>{selectedFile}</Typography>} */}
            <Button
              onClick={handleBulkUpload}
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: "40px",
              }}
            >
              Submit
            </Button>
          </Box>
        </Modal>
        <Modal open={open} onClose={() => setOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 350,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <IconButton
              sx={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                width: 60,
                height: 60,
                backgroundColor: "#00C853",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "auto",
              }}
            >
              <Typography sx={{ fontSize: 40, color: "white" }}>✔</Typography>
            </Box>
            <Typography variant="h6" sx={{ mt: 2 }}>
              New Vehicle Added Successfully!
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 3,
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: "40px",
              }}
              onClick={() => setOpen(false)}
            >
              Continue to Dashboard
            </Button>
          </Box>
        </Modal>
        <Snackbar
          open={showErrorSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowErrorSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={isSuccessSnackbar ? "success" : "error"}
            sx={{
              backgroundColor: isSuccessSnackbar ? "#4caf50" : "#f44336",
              color: "#fff",
              display: "flex",
              alignItems: "center",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default FleetDailyReport;
